/**
 * Auth Lambda session: SAML completes on observability-api; the SPA receives the API JWT via ?token=.
 *
 * Plan: docs/dashboard_impl_plan.md — D1.9–D1.15
 */

let apiJwt: string | null = null;
let tokenExpiresAt: number | null = null;

const SKEW_MS = 60_000;

function getApiBase(): string {
  const url = import.meta.env.VITE_API_URL;
  if (url === undefined || url === '') {
    throw new Error('VITE_API_URL is not configured');
  }
  return url.replace(/\/$/, '');
}

/**
 * Redirects the browser to Auth Lambda login (SAML with Identity Center).
 */
export function login(): void {
  window.location.href = `${getApiBase()}/auth/login?app=observability`;
}

function base64UrlDecode(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (padded.length % 4)) % 4;
  return atob(padded + '='.repeat(padLen));
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid JWT format');
  }
  const json = base64UrlDecode(parts[1]);
  return JSON.parse(json) as Record<string, unknown>;
}

function jwtExpMs(token: string): number | null {
  try {
    const p = decodeJwtPayload(token);
    const exp = p.exp;
    if (typeof exp === 'number') {
      return exp * 1000;
    }
    return null;
  } catch {
    return null;
  }
}

function parseUserFromToken(token: string): { email: string; name: string } {
  const payload = decodeJwtPayload(token);
  const sub = payload.sub;
  const name = payload.name;
  return {
    email: typeof sub === 'string' ? sub : 'Unknown',
    name: typeof name === 'string' ? name : 'Unknown User',
  };
}

/**
 * If `?token=` is present, stores the JWT in memory and removes it from the URL.
 * @returns User claims when a token was ingested; otherwise null.
 */
export function handleTokenFromUrl(): { email: string; name: string } | null {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (token === null || token === '') {
    return null;
  }

  const claims = parseUserFromToken(token);
  apiJwt = token;
  tokenExpiresAt = jwtExpMs(token) ?? Date.now() + 8 * 60 * 60 * 1000;

  const url = new URL(window.location.href);
  url.searchParams.delete('token');
  const qs = url.searchParams.toString();
  const pathAndQuery = `${url.pathname}${qs !== '' ? `?${qs}` : ''}${url.hash}`;
  window.history.replaceState({}, '', pathAndQuery);

  return claims;
}

/**
 * Returns the Auth Lambda JWT for `Authorization: Bearer` if still valid.
 */
export async function getToken(): Promise<string | null> {
  if (apiJwt !== null && tokenExpiresAt !== null && Date.now() < tokenExpiresAt - SKEW_MS) {
    return apiJwt;
  }
  void (await refresh());
  if (apiJwt !== null && tokenExpiresAt !== null && Date.now() < tokenExpiresAt - SKEW_MS) {
    return apiJwt;
  }
  return null;
}

/**
 * No refresh token in the Auth Lambda model; expired sessions require login again.
 */
export function refresh(): Promise<boolean> {
  apiJwt = null;
  tokenExpiresAt = null;
  return Promise.resolve(false);
}

/**
 * Clears the session and redirects through Auth Lambda logout (IdP logout).
 */
export function logout(): void {
  apiJwt = null;
  tokenExpiresAt = null;
  window.location.href = `${getApiBase()}/auth/logout?app=observability`;
}

export function isAuthenticated(): boolean {
  return apiJwt !== null && tokenExpiresAt !== null && Date.now() < tokenExpiresAt - SKEW_MS;
}

export function getCurrentUser(): { email: string; name: string } | null {
  if (apiJwt === null) {
    return null;
  }
  try {
    return parseUserFromToken(apiJwt);
  } catch {
    return null;
  }
}
